import React, { useState, useEffect } from "react";
import API_BASE_URL from"../Config/config";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  CircularProgress,
  Container,
  Paper,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem
} from "@mui/material";
import { Cancel, CheckCircle, Schedule, Search, Assignment, History, SupportAgent, Person, Visibility } from "@mui/icons-material";
import { viewResume } from "../services/fileService";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, api } from "../context/AuthContext";

function MyApplications() {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Auto-update current time every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000); // 30 sec

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function fetchApplications() {
      try {
        const response = await api.get(
          "/applications/candidate/applications"
        );
        const allApps = response.data;
        setApplications(allApps);
      } catch (error) {
        console.error("Failed to fetch applications:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchApplications();
  }, []);

  useEffect(() => {
    const filtered = applications.filter((app) => {
      const search = searchTerm.toLowerCase();

      const matchesSearch =
        app.job?.title?.toLowerCase().includes(search) ||
        app.job?.company_name?.toLowerCase().includes(search) ||
        getStatusLabel(app.status).toLowerCase().includes(search);

      const matchesStatus =
        statusFilter === "All" ||
        getFilterStatusLabel(app.status) === statusFilter;

      return matchesSearch && matchesStatus;
    });

    setFilteredApplications(filtered);
    setTotalPages(Math.ceil(filtered.length / 10));
    setPage(1);
  }, [searchTerm, statusFilter, applications]);

  function getStatusColor(status) {
    const s = status?.toLowerCase() || '';

    // Exact colors from the recruiter flow design
    if (s.includes('selected')) return { bg: '#ebfbee', text: '#2f855a' }; // Light green
    if (s.includes('offer released')) return { bg: '#ebf8ff', text: '#2b6cb0' }; // Light blue
    if (s.includes('terminated')) return { bg: '#fff5f5', text: '#c53030' }; // Light red
    if (s.includes('hr scheduled') || s.includes('hr_scheduled') || s.includes('ai_hr_interview') || s.includes('hr_l2_interview')) return { bg: '#faf5ff', text: '#6b46c1' }; // Light purple
    if (s.includes('technical cancelled') || s.includes('technical_cancelled')) return { bg: '#fffaf0', text: '#c05621' }; // Light orange
    if (s.includes('ai coding scheduled')) return { bg: '#fffff0', text: '#b7791f' }; // Light yellow

    // Map existing statuses
    if (s.includes('pending')) return { bg: '#fff8e1', text: '#f57f17' };
    if (s.includes('shortlisted')) return { bg: '#e3f2fd', text: '#1976d2' };
    if (s.includes('rejected')) return { bg: '#ffebee', text: '#d32f2f' };
    if (s.includes('completed')) return { bg: '#e0f2f1', text: '#4338CA' };
    if (s.includes('scheduled')) return { bg: '#e3f2fd', text: '#1976d2' };

    return { bg: '#f5f5f5', text: '#616161' }; // Grey default
  }

  const getNextAction = (app) => {
    const currentStage = app.current_stage || app.currentStage;
    const now = currentTime;

    if (currentStage === "technical_interview") {
      const scheduledAt =
        app.technicalInterview?.scheduledAt ||
        app.technicalInterview?.scheduled_at;

      if (!scheduledAt) return null;

      const scheduledTime = new Date(scheduledAt);
      const tenMinutesAfter = new Date(scheduledTime.getTime() + 10 * 60 * 1000);

      const isBeforeTime = now < scheduledTime;
      const isWithinWindow = now >= scheduledTime && now <= tenMinutesAfter;
      const isExpired = now > tenMinutesAfter;

      let buttonLabel = "Start Technical Interview";
      if (isBeforeTime) buttonLabel = "Wait for Scheduled Time";
      if (isExpired) buttonLabel = "Interview Window Expired";

      return (
        <Box>
          {/* Scheduled Info */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: "#e8f5e9",
              borderRadius: 1,
              borderLeft: "4px solid #4caf50",
            }}
          >
            <Typography
              variant="subtitle2"
              color="success.main"
              sx={{ fontWeight: "bold", mb: 0.5 }}
            >
              📅 Technical Interview Scheduled
            </Typography>

            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Date:</strong> {scheduledTime.toLocaleString()}
            </Typography>

            {app.technicalInterview?.meetingLink && (
              <Typography variant="body2">
                <strong>Link:</strong>{" "}
                <a
                  href={app.technicalInterview.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {app.technicalInterview.meetingLink}
                </a>
              </Typography>
            )}

            {app.technicalInterview?.interview_mode && (
              <Typography variant="body2">
                <strong>Mode:</strong> {app.technicalInterview.interview_mode}
              </Typography>
            )}

            {isExpired && (
              <Typography variant="body2" color="error">
                ⚠️ You missed the interview window (10 minutes allowed).
              </Typography>
            )}
          </Box>

          <Button
            size="small"
            variant="contained"
            fullWidth
            disabled={!isWithinWindow} // 🔹 Disabled if outside 10-min window
            onClick={() =>
              navigate(`/candidate/technical-interview/${app.id}`)
            }
          >
            {buttonLabel}
          </Button>
        </Box>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const getStatusLabel = (status) => {
    const map = {
      // Coding test
      ai_coding_scheduled: "AI Coding Assessment Scheduled",
      ai_coding_completed: "AI Coding Completed",
      terminated: "Interview Terminated",
      application_shortlisted: "Shortlisted",
      // Legacy generic statuses (backward compat)
      technical_scheduled: "Technical Interview Scheduled",
      technical_completed: "Technical Interview Completed",
      technical_cancelled: "Technical Interview Cancelled",
      technical_interview_completed: "Technical Interview Completed",
      hr_scheduled: "HR Interview Scheduled",
      hr_completed: "HR Interview Completed",
      hr_cancelled: "HR Interview Cancelled",
      hr_interview_completed: "HR Interview Completed",
      // Type-specific statuses
      ai_technical_interview_scheduled: "AI Technical Interview Scheduled",
      ai_technical_interview_completed: "AI Technical Interview Completed",
      technical_l2_interview_scheduled: "Technical L2 Interview Scheduled",
      technical_l2_interview_completed: "Technical L2 Interview Completed",
      ai_hr_interview_scheduled: "AI HR Interview Scheduled",
      ai_hr_interview_completed: "AI HR Interview Completed",
      hr_l2_interview_scheduled: "HR L2 Interview Scheduled",
      hr_l2_interview_completed: "HR L2 Interview Completed",
      technical_l2_interview_cancelled: "Technical L2 Interview Cancelled",
      hr_l2_interview_cancelled: "HR L2 Interview Cancelled",
      candidate_selected: "Selected",
      candidate_rejected: "Rejected",
      offer_released: "Offer Released",
    };

    return map[status] || "Pending Review";
  };

  const getFilterStatusLabel = (status) => {
    const s = (status || "").toLowerCase();

    if (s.includes("ai_coding_scheduled"))
      return "AI Coding Assessment Scheduled";

    if (s.includes("technical_cancelled"))
      return "Technical Interview Cancelled";

    if (s.includes("hr_cancelled"))
      return "HR Interview Cancelled";

    if (s.includes("offer_released"))
      return "Offer Released";

    if (s.includes("pending"))
      return "Pending";

    if (s.includes("shortlisted"))
      return "Shortlisted";

    if (s.includes("selected"))
      return "Selected";

    if (s.includes("rejected"))
      return "Rejected";

    if (s.includes("terminated"))
      return "Terminated";

    if (s.includes("completed"))
      return "Completed";

    if (s.includes("scheduled"))
      return "Interview Scheduled";

    return "All";
  };

  const STATUS_OPTIONS = [
              "All",
              "Pending",
              "Shortlisted",
              "Selected",
              "Offer Released",
              "Rejected",
              "Terminated",
              "Completed",
              "Interview Scheduled",
              "Technical Interview Cancelled",
              "HR Interview Cancelled",
              "AI Coding Assessment Scheduled",
            ];

  const paginatedApps = filteredApplications.slice((page - 1) * 10, page * 10);

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: "#4F46E5", fontWeight: "bold", mb: 1 }}
          >
            My Job Applications
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track the status of all your submitted job applications.
          </Typography>
        </Box>
        <TextField
          size="small"
          placeholder="Search by name, email, job..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            width: 350,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: 'white',
              '& fieldset': { borderColor: '#e2e8f0' },
              '&:hover fieldset': { borderColor: '#cbd5e0' },
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#a0aec0', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ 
          border: '1px solid #e5e7eb', 
          borderRadius: '18px', 
          overflow: 'hidden',
          boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#1a202c', py: 2 }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#1a202c', py: 2 }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#1a202c', py: 2 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#1a202c', py: 2 }}>Applied On</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', color: '#1a202c', py: 2 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedApps.length > 0 ? (
              paginatedApps.map((app) => {
                const statusStyles = getStatusColor(app.status);
                return (
                  <TableRow key={app.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ py: 2.5 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ color: '#4F46E5', fontWeight: 'bold', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/jobs/${app.job?.id || app.job_id}`)}
                      >
                        {app.job?.title || "Unknown Position"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: '#1a202c', fontWeight: 500 }}>{app.job?.company_name || "Unknown Company"}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(app.status)}
                        size="small"
                        sx={{
                          fontWeight: 'bold',
                          borderRadius: "4px",
                          px: 1,
                          height: "24px",
                          fontSize: "0.75rem",
                          backgroundColor: statusStyles.bg,
                          color: statusStyles.text,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#1a202c', fontSize: '0.875rem' }}>
                      {new Date(app.created_at || app.createdAt).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility sx={{ fontSize: '16px !important' }} />}
                        onClick={() => {
                          if (app.resume_url) {
                            window.open(app.resume_url, '_blank');
                          } else {
                            viewResume(app.resume_key || app.resume || app.resume_path, app.resume_original_name);
                          }
                        }}
                        sx={{ 
                          textTransform: "none", 
                          fontWeight: 'bold',
                          color: '#4F46E5',
                          borderColor: '#4F46E5',
                          borderRadius: '8px',
                          '&:hover': { 
                            borderColor: '#4338CA', 
                            bgcolor: 'rgba(79,70,229,0.04)' 
                          }
                        }}
                      >
                        View Resume
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                  <Typography variant="body1" color="text.secondary">
                    {searchTerm ? "No applications found matching your search." : "You haven't applied for any jobs yet."}
                  </Typography>
                  {!searchTerm && (
                    <Button
                      variant="contained"
                      sx={{ 
                        mt: 2, 
                        bgcolor: '#4F46E5', 
                        fontWeight: 'bold',
                        borderRadius: '8px',
                        textTransform: 'none',
                        '&:hover': { bgcolor: '#4338CA' } 
                      }}
                      onClick={() => navigate("/jobs")}
                    >
                      Browse Available Jobs
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {paginatedApps.length > 0 ? (page - 1) * 10 + 1 : 0} to {Math.min(page * 10, filteredApplications.length)} of {filteredApplications.length} applications
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => {
            setPage(value);
          }}
          shape="rounded"
          color="primary"
          sx={{
            '& .MuiPaginationItem-root.Mui-selected': {
              backgroundColor: '#4F46E5',
              '&:hover': {
                backgroundColor: '#4338CA',
              }
            }
          }}
        />
      </Box>
    </Container>
  );
}

export default MyApplications;
